import { memo, useState, useCallback } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { NodeData } from '@/types/workflow';
import { Brain, Copy, Trash2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface LLMNodeProps extends NodeProps {
  data: NodeData;
}

const providerModels = {
  openai: [
    'gpt-4o',
    'gpt-4o-mini', 
    'gpt-4-turbo',
    'gpt-4',
    'gpt-3.5-turbo'
  ],
  anthropic: [
    'claude-3-5-sonnet-20241022',
    'claude-3-5-haiku-20241022',
    'claude-3-opus-20240229',
    'claude-3-sonnet-20240229',
    'claude-3-haiku-20240307'
  ],
  google: [
    'gemini-1.5-pro',
    'gemini-1.5-flash',
    'gemini-pro',
    'gemini-pro-vision'
  ]
};

const modelDescriptions = {
  // OpenAI Models
  'gpt-4o': 'Most advanced model - best for complex reasoning',
  'gpt-4o-mini': 'Fast and efficient - great for most tasks', 
  'gpt-4-turbo': 'High performance with 128k context',
  'gpt-4': 'Reliable and powerful for complex tasks',
  'gpt-3.5-turbo': 'Fast and cost-effective',
  
  // Anthropic Models
  'claude-3-5-sonnet-20241022': 'Latest Claude - excellent for analysis',
  'claude-3-5-haiku-20241022': 'Fast Claude - great for quick tasks',
  'claude-3-opus-20240229': 'Most capable Claude - best reasoning',
  'claude-3-sonnet-20240229': 'Balanced performance and speed',
  'claude-3-haiku-20240307': 'Fastest Claude model',
  
  // Google Models
  'gemini-1.5-pro': 'Advanced reasoning with 2M context',
  'gemini-1.5-flash': 'Fast responses with long context',
  'gemini-pro': 'Versatile and reliable',
  'gemini-pro-vision': 'Includes vision capabilities'
};

const getDefaultModel = (provider: string): string => {
  switch (provider) {
    case 'openai': return 'gpt-4o-mini';
    case 'anthropic': return 'claude-3-5-sonnet-20241022';
    case 'google': return 'gemini-1.5-flash';
    default: return 'gpt-4o-mini';
  }
};

export const LLMNode = memo(({ data, selected, id }: LLMNodeProps) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const { getNodes, setNodes, getEdges, setEdges } = useReactFlow();
  const nodeColor = '#8b5cf6'; // Purple for LLM

  const updateNodeData = useCallback((updates: Partial<NodeData>) => {
    const nodes = getNodes();
    const updatedNodes = nodes.map(node => {
      if (node.id === id) {
        return {
          ...node,
          data: {
            ...node.data,
            ...updates
          }
        };
      }
      return node;
    });
    setNodes(updatedNodes);
  }, [id, getNodes, setNodes]);

  const handleDuplicate = useCallback(() => {
    const nodes = getNodes();
    const currentNode = nodes.find(node => node.id === id);
    if (currentNode) {
      const newNode = {
        ...currentNode,
        id: `${currentNode.type}_${Date.now()}`,
        position: {
          x: currentNode.position.x + 50,
          y: currentNode.position.y + 50,
        },
      };
      setNodes([...nodes, newNode]);
      // toast({
      //   title: "Node duplicated",
      //   description: "The node has been duplicated successfully",
      // });
    }
  }, [id, getNodes, setNodes]);


  const handleDelete = useCallback(() => {
    const nodes = getNodes();
    const edges = getEdges();
    
    const updatedNodes = nodes.filter(node => node.id !== id);
    const updatedEdges = edges.filter(edge => edge.source !== id && edge.target !== id);
    
    setNodes(updatedNodes);
    setEdges(updatedEdges);
    
    // toast({
    //   title: "Node deleted",
    //   description: "The node and its connections have been removed",
    // });
  }, [id, getNodes, getEdges, setNodes, setEdges]);

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setShowToolbar(true)}
      onMouseLeave={() => setShowToolbar(false)}
    >
      {/* Gumloop-style Toolbar */}
      {(showToolbar || selected) && (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-10">
          <div className="flex items-center gap-1 bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg px-2 py-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-muted"
              onClick={handleDuplicate}
            >
              <Copy className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
              onClick={handleDelete}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      <Card 
        className={`min-w-[350px] backdrop-blur-md bg-background/75 border-purple-500/30 transition-all ${
          selected ? 'ring-2 ring-purple-500/50 shadow-lg shadow-purple-500/20' : 'shadow-md hover:shadow-lg'
        }`}
      >
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-md flex items-center justify-center"
            style={{ backgroundColor: nodeColor + '20' }}
          >
            <Brain className="w-4 h-4" style={{ color: nodeColor }} />
          </div>
          <div className="flex-1">
            <div className="font-medium text-sm">{data.label || 'LLM'}</div>
            <div className="text-xs text-muted-foreground">Language Model</div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Provider</Label>
            <Select 
              value={data.provider || 'openai'}
              onValueChange={(value) => updateNodeData({ 
                provider: value, 
                model: getDefaultModel(value) 
              })}
            >
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    OpenAI
                  </div>
                </SelectItem>
                <SelectItem value="anthropic">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                    Anthropic
                  </div>
                </SelectItem>
                <SelectItem value="google">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    Google
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Model</Label>
            <Select 
              value={data.model || getDefaultModel(data.provider || 'openai')}
              onValueChange={(value) => updateNodeData({ model: value })}
            >
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {providerModels[data.provider as keyof typeof providerModels]?.map((model) => (
                  <SelectItem key={model} value={model}>
                    <div className="flex flex-col">
                      <div className="font-medium">{model}</div>
                      <div className="text-xs text-muted-foreground">
                        {modelDescriptions[model as keyof typeof modelDescriptions]}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="p-2 bg-green-50 dark:bg-green-950/20 rounded-md border border-green-200 dark:border-green-800">
          <div className="text-xs text-green-700 dark:text-green-300">
            ✅ AI powered by platform - no API key needed
          </div>
        </div>
        
        <div>
          <Label className="text-xs">System Prompt</Label>
          <Textarea
            placeholder="You are a helpful assistant..."
            value={data.systemPrompt || ''}
            onChange={(e) => updateNodeData({ systemPrompt: e.target.value })}
            className="min-h-[60px] resize-none text-xs"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Temperature</Label>
            <Input
              type="number"
              min="0"
              max="2"
              step="0.1"
              value={data.temperature || 0.7}
              onChange={(e) => updateNodeData({ temperature: parseFloat(e.target.value) || 0.7 })}
              className="h-8"
            />
          </div>
          <div>
            <Label className="text-xs">Max Tokens</Label>
            <Input
              type="number"
              min="1"
              max="4000"
              value={data.maxTokens || 1000}
              onChange={(e) => updateNodeData({ maxTokens: parseInt(e.target.value) || 1000 })}
              className="h-8"
            />
          </div>
        </div>
      </CardContent>
      
      {/* Input Handle - Positioned on left edge */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="custom-handle"
      />
      
      {/* Output Handle - Positioned on right edge */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="custom-handle"
        style={{
          backgroundColor: '#8b5cf6',
          boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
        }}
      />
      </Card>
    </div>
  );
});

LLMNode.displayName = 'LLMNode';
